import React, { useEffect, useRef, useState, useCallback } from "react";
import { gsap } from "../../animations/gsap.config";
import { ScrollTrigger } from "../../animations/gsap.config";
import homeData from "../../data/home.json";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  CheckCircle,
  AlertCircle,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { FaLinkedinIn } from "react-icons/fa";
import { useMutation, useQuery } from "@tanstack/react-query";
import api from "../../api/api";
import toast from "react-hot-toast";

/* ─────────────────────────────────────────────
 FORM FIELD
───────────────────────────────────────────── */
const FormField = ({ field, value, onChange, error }) => {
  const [focused, setFocused] = useState(false);
  const ref = useRef(null);
  const lineRef = useRef(null);

  const handleFocus = () => {
    setFocused(true);
    gsap.to(lineRef.current, {
      scaleX: 1,
      duration: 0.4,
      ease: "power3.out",
    });
    gsap.to(ref.current, {
      borderColor: "rgba(29,78,216,0.5)",
      duration: 0.3,
    });
  };

  const handleBlur = () => {
    setFocused(false);
    if (!value) {
      gsap.to(lineRef.current, {
        scaleX: 0,
        duration: 0.3,
        ease: "power2.in",
      });
    }
    gsap.to(ref.current, {
      borderColor: "rgba(203,213,225,0.3)",
      duration: 0.3,
    });
  };

  const baseClass = `
    w-full bg-transparent text-gray-900 text-sm
    placeholder-gray-500 outline-none resize-none
    transition-colors duration-300
  `;

  return (
    <div className={`${field.half ? "" : "col-span-full"} space-y-1.5`}>
      <label className="block text-xs font-semibold text-gray-600 tracking-wide">
        {field.label}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
      </label>

      <div
        ref={ref}
        className="relative rounded-xl overflow-hidden"
        style={{
          background: "rgba(248,250,252,0.8)",
          border: error
            ? "1px solid rgba(239,68,68,0.4)"
            : "1px solid rgba(203,213,225,0.3)",
        }}
      >
        {field.type === "textarea" ? (
          <textarea
            id={field.id}
            name={field.id}
            placeholder={field.placeholder}
            rows={5}
            maxLength={field.maxLength}
            required={field.required}
            value={value}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={`${baseClass} px-4 py-3`}
          />
        ) : (
          <input
            id={field.id}
            type={field.type}
            name={field.id}
            placeholder={field.placeholder}
            required={field.required}
            value={value}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={`${baseClass} px-4 py-3`}
          />
        )}

        {field.type === "textarea" && field.maxLength && (
          <div className="absolute bottom-2 right-3 text-[10px] text-gray-500 font-mono">
            {value.length} / {field.maxLength}
          </div>
        )}

        <div
          ref={lineRef}
          className="absolute bottom-0 left-0 right-0 h-[2px] origin-left"
          style={{
            background: "linear-gradient(90deg, #1d4ed8, #f59e0b)",
            transform: "scaleX(0)",
          }}
        />
      </div>

      {error && (
        <p className="flex items-center gap-1 text-xs text-red-500">
          <AlertCircle size={11} />
          {error}
        </p>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
 CONTACT INFO CARD
───────────────────────────────────────────── */
const InfoCard = ({ icon: Icon, label, children, color, href }) => {
  const ref = useRef(null);

  const handleEnter = () => {
    gsap.to(ref.current, {
      x: 4,
      borderColor: `${color}40`,
      backgroundColor: `${color}06`,
      duration: 0.3,
      ease: "power2.out",
    });
  };
  const handleLeave = () => {
    gsap.to(ref.current, {
      x: 0,
      borderColor: "rgba(203,213,225,0.3)",
      backgroundColor: "rgba(248,250,252,0.8)",
      duration: 0.3,
      ease: "power2.out",
    });
  };

  const Wrapper = href ? "a" : "div";
  const props = href
    ? {
        href,
        target: href.startsWith("http") ? "_blank" : undefined,
        rel: "noopener noreferrer",
      }
    : {};

  return (
    <Wrapper
      ref={ref}
      {...props}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className="flex items-start gap-4 p-4 rounded-2xl transition-shadow duration-300"
      style={{
        background: "rgba(248,250,252,0.8)",
        border: "1px solid rgba(203,213,225,0.3)",
      }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}20`, border: `1px solid ${color}30` }}
      >
        <Icon size={18} style={{ color }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold tracking-wider text-gray-500 uppercase mb-1">
          {label}
        </p>
        <div className="text-sm text-gray-700 leading-relaxed">{children}</div>
      </div>
    </Wrapper>
  );
};

/* ─────────────────────────────────────────────
 MAP EMBED
───────────────────────────────────────────── */
/* ─────────────────────────────────────────────
 MAP EMBED
───────────────────────────────────────────── */
const MapEmbed = () => {
  const containerRef = useRef(null);
  const hasRun = useRef(false);

  // Correctly structures and encodes the new plus code address string safely
  const queryLocation = encodeURIComponent("XQRM+F32, Addis Ababa");
  const mapUrl = `https://maps.google.com/maps?q=${queryLocation}&t=&z=16&ie=UTF8&iwloc=&output=embed`;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: container,
        start: "top 85%",
        onEnter: () => {
          if (hasRun.current) return;
          hasRun.current = true;
          gsap.fromTo(
            container,
            { opacity: 0, scale: 0.97 },
            { opacity: 1, scale: 1, duration: 0.7, ease: "power3.out" },
          );
        },
      });
    }, container);

    return () => ctx.revert();
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative rounded-2xl overflow-hidden opacity-0 shadow-sm"
      style={{
        height: "320px",
        border: "1px solid rgba(29,78,216,0.15)",
      }}
    >
      <iframe
        title="Agora Consulting Office Location"
        src={mapUrl}
        width="100%"
        height="100%"
        style={{ border: 0 }}
        allowFullScreen=""
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />

      {/* Floating Location Overlay Badge */}
      <div className="absolute bottom-4 left-4 right-4 z-20">
        <div className="px-3 py-2 rounded-xl flex items-center gap-2 bg-slate-900/90 backdrop-blur-md border border-blue-500/30 shadow-md">
          <MapPin size={14} className="text-blue-400 shrink-0" />
          <span className="text-xs text-slate-200 font-medium truncate">
            Agora Consulting, XQRM+F32, Addis Ababa
          </span>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
 LEFT INFO PANEL
───────────────────────────────────────────── */
const InfoPanel = ({ data }) => {
  const panelRef = useRef(null);
  const headRef = useRef(null);
  const cardsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: panelRef.current,
          start: "top 72%",
        },
      });

      tl.fromTo(
        headRef.current?.children ?? [],
        { y: 30, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.1,
          duration: 0.6,
          ease: "power3.out",
        },
      ).fromTo(
        cardsRef.current?.children ?? [],
        { y: 20, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          stagger: 0.08,
          duration: 0.5,
          ease: "power3.out",
        },
        "-=0.2",
      );
    }, panelRef);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={panelRef} className="space-y-8">
      <div ref={headRef} className="space-y-2">
        <h2
          className="font-display font-black text-gray-900 leading-tight"
          style={{ fontSize: "clamp(2rem, 3.5vw, 2.75rem)" }}
        >
          {data?.headline || "Get In Touch With Us"}
        </h2>
      </div>

      <div ref={cardsRef} className="space-y-3">
        {data?.phone && (
          <InfoCard
            icon={Phone}
            label="Primary Phone"
            color="#3b82f6"
            href={`tel:${data.phone}`}
          >
            <span className="hover:text-blue-600 transition-colors">
              {data.phone}
            </span>
          </InfoCard>
        )}

        {data?.phone2 && (
          <InfoCard
            icon={Phone}
            label="Secondary Phone"
            color="#3b82f6"
            href={`tel:${data.phone2}`}
          >
            <span className="hover:text-blue-600 transition-colors">
              {data.phone2}
            </span>
          </InfoCard>
        )}

        {data?.email && (
          <InfoCard
            icon={Mail}
            label="Email Address"
            color="#f59e0b"
            href={`mailto:${data.email}`}
          >
            <span className="hover:text-amber-600 transition-colors break-all">
              {data.email}
            </span>
          </InfoCard>
        )}

        <InfoCard icon={MapPin} label="Our Office" color="#6366f1">
          <p className="font-medium text-slate-800">
            {data?.address || "Century Executive Tower"}
          </p>
          <p className="text-slate-500 text-xs">
            {data?.city || "Addis Ababa, Ethiopia"}
          </p>
          {data?.poBox && (
            <p className="text-slate-400 text-xs">P.O. Box: {data.poBox}</p>
          )}
        </InfoCard>

        <InfoCard icon={Clock} label="Office Hours" color="#10b981">
          <p>Monday - Friday: 8:00 AM - 5:00 PM</p>
        </InfoCard>

        {data?.linkedin && (
          <a
            href={data.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 px-4 py-3 rounded-xl border border-blue-500/20 bg-blue-500/5 hover:bg-blue-500/10 transition-all group"
          >
            <FaLinkedinIn size={16} className="text-blue-600" />
            <span className="text-sm text-slate-700 group-hover:text-slate-900 font-medium transition-colors">
              Follow us on LinkedIn
            </span>
            <ExternalLink size={12} className="text-slate-400 ml-auto" />
          </a>
        )}
      </div>

      <MapEmbed />
    </div>
  );
};

/* ─────────────────────────────────────────────
 CONTACT FORM
───────────────────────────────────────────── */
const ContactForm = ({ formData }) => {
  const formRef = useRef(null);
  const successRef = useRef(null);

  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const submitContactMutation = useMutation({
    mutationFn: (payload) => api.post("/api/contact/submit", payload),
    onSuccess: () => {
      toast.success("Message submitted successfully!");
      setStatus("success");
    },
    onError: (err) => {
      toast.error(
        `Submission failed: ${err?.response?.data?.message || err.message}`,
      );
      setStatus("idle");
    },
  });

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        formRef.current,
        { x: 40, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.9,
          ease: "power3.out",
          scrollTrigger: {
            trigger: formRef.current,
            start: "top 72%",
          },
        },
      );
    }, formRef);
    return () => ctx.revert();
  }, []);

  const validate = useCallback(() => {
    const errs = {};
    formData?.fields?.forEach((f) => {
      if (f.required && !values[f.id]) {
        errs[f.id] = `${f.label} is required`;
      }
      if (
        f.type === "email" &&
        values[f.id] &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values[f.id])
      ) {
        errs[f.id] = "Please enter a valid email address";
      }
    });
    return errs;
  }, [values, formData]);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setValues((p) => ({ ...p, [name]: value }));
      if (errors[name]) {
        setErrors((p) => ({ ...p, [name]: "" }));
      }
    },
    [errors],
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const errs = validate();
      if (Object.keys(errs).length) {
        setErrors(errs);
        gsap.to(formRef.current, {
          x: [-8, 8, -6, 6, -3, 3, 0],
          duration: 0.4,
          ease: "none",
        });
        return;
      }

      const payload = {
        name: `${values.firstName || ""} ${values.lastName || ""}`.trim(),
        email: values.email,
        phonenumber: values.phone,
        subject: values.subject,
        message: values.message,
      };

      setStatus("loading");
      submitContactMutation.mutate(payload);
    },
    [validate, values, submitContactMutation],
  );

  useEffect(() => {
    if (status === "success" && successRef.current) {
      gsap.fromTo(
        successRef.current,
        { scale: 0.9, opacity: 0, y: 20 },
        { scale: 1, opacity: 1, y: 0, duration: 0.6, ease: "back.out(1.7)" },
      );
    }
  }, [status]);

  if (status === "success") {
    return (
      <div
        ref={successRef}
        className="h-full flex flex-col items-center justify-center text-center p-10 rounded-3xl space-y-5"
        style={{
          background: "rgba(16,185,129,0.05)",
          border: "1px solid rgba(16,185,129,0.2)",
        }}
      >
        <div className="w-20 h-20 rounded-full bg-emerald-500/20 border-2 border-emerald-500/40 flex items-center justify-center mx-auto">
          <CheckCircle size={36} className="text-emerald-500" />
        </div>
        <div className="space-y-2">
          <h3 className="text-xl font-bold text-slate-900">Message Sent!</h3>
          <p className="text-slate-500 text-sm max-w-xs">
            {formData?.successMessage ||
              "Thank you. We will get back to you shortly."}
          </p>
        </div>
        <button
          onClick={() => {
            setStatus("idle");
            setValues({});
          }}
          className="text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors underline"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <div
      ref={formRef}
      className="rounded-3xl p-7 sm:p-8 space-y-6 opacity-0"
      style={{
        background: "rgba(255, 255, 255, 0.8)",
        border: "1px solid rgba(203,213,225,0.4)",
        backdropFilter: "blur(16px)",
      }}
    >
      <div className="space-y-1">
        <h3 className="font-bold text-gray-900 text-xl">Send Us a Message</h3>
        <p className="text-gray-500 text-sm">
          We typically respond within 24 hours.
        </p>
      </div>

      <div
        className="h-px w-full"
        style={{
          background:
            "linear-gradient(to right, rgba(29,78,216,0.2), rgba(245,158,11,0.1), transparent)",
        }}
      />

      <form onSubmit={handleSubmit} noValidate>
        <div className="grid grid-cols-2 gap-4">
          {formData?.fields?.map((field) => (
            <FormField
              key={field.id}
              field={field}
              value={values[field.id] || ""}
              onChange={handleChange}
              error={errors[field.id]}
            />
          ))}
        </div>

        <div className="mt-6">
          <button
            type="submit"
            disabled={status === "loading"}
            className="group w-full relative inline-flex items-center justify-center gap-2.5 px-7 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-sm overflow-hidden transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
          >
            {status === "loading" ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Send size={15} />
                <span>{formData?.submitText || "Submit Message"}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

/* ─────────────────────────────────────────────
 BG EFFECTS (CLEAN SPLINE - NO GRID OVERLAYS)
───────────────────────────────────────────── */
const ContactBg = () => {
  const meshRef = useRef(null);

  useEffect(() => {
    gsap.to(meshRef.current, {
      opacity: 0.6,
      scale: 1.05,
      duration: 6,
      ease: "sine.inOut",
      repeat: -1,
      yoyo: true,
    });
  }, []);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
      <div
        ref={meshRef}
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 60% 50% at 15% 45%, rgba(59,130,246,0.06) 0%, transparent 65%),
            radial-gradient(ellipse 50% 50% at 85% 55%, rgba(245,158,11,0.04) 0%, transparent 60%)
          `,
          opacity: 0.4,
        }}
      />
    </div>
  );
};

/* ─────────────────────────────────────────────
 MAIN CONTACT SECTION
───────────────────────────────────────────── */
const ContactSection = () => {
  const { contact } = homeData;
  const sectionRef = useRef(null);

  const { data: companyinfo } = useQuery({
    queryKey: ["companyInfo"],
    queryFn: () => api.get("/api/companyinfo/public").then((res) => res.data),
  });

  return (
    <section
      ref={sectionRef}
      id="contact"
      className="relative overflow-hidden py-20 bg-slate-50/60"
    >
      <ContactBg />

      <div className="container mx-auto px-4 relative z-10 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-20">
          <InfoPanel data={companyinfo} />
          <ContactForm formData={contact?.form} />
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
